import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useWebSocket, { ReadyState } from 'react-use-websocket';
import { logError } from '@zg-rentals/log-error';
import type { AuthenticationResponse, SubscriptionRequest, WebSocketLogger, WebSocketMessage } from './types';

// Configuration
const RECONNECT_INTERVAL = 3 * 1000; // 3 seconds in ms
const MAX_RECONNECT_INTERVAL = 5 * 60 * 1000; // 5 minutes in ms
const MAX_RECONNECT_ATTEMPTS = 15;

// WebSocket close code descriptions for better debugging
const CLOSE_CODE_DESCRIPTIONS: Record<number, string> = {
  1000: 'Normal closure',
  1001: 'Going away (page unload)',
  1002: 'Protocol error',
  1003: 'Unsupported data type',
  1004: 'Reserved',
  1005: 'No status code',
  1006: 'Abnormal closure (network issue)', // Very common on mobile
  1007: 'Invalid data',
  1008: 'Policy violation',
  1009: 'Message too large',
  1010: 'Extension required',
  1011: 'Internal server error',
  1012: 'Service restart',
  1013: 'Try again later',
  1014: 'Bad gateway',
  1015: 'TLS handshake failure',
};

const getWebSocketCloseCodeDescription = (code: number): string =>
  CLOSE_CODE_DESCRIPTIONS[code] || `Unknown code ${code}`;

export const safeJsonParse = (data: string): unknown => {
  try {
    return JSON.parse(data);
  } catch {
    return data;
  }
};

export const isAuthenticationResponse = (data: unknown): data is AuthenticationResponse =>
  data != null &&
  typeof data === 'object' &&
  'type' in data &&
  (data as { type: unknown }).type === 'id' &&
  'userToken' in data &&
  typeof (data as { userToken: unknown }).userToken === 'string' &&
  'sessionId' in data &&
  typeof (data as { sessionId: unknown }).sessionId === 'string';

export const isWebSocketMessage = (data: unknown): data is WebSocketMessage => {
  if (data == null || typeof data !== 'object' || isAuthenticationResponse(data)) return false;
  const record = data as Record<string, unknown>;
  const hasTopic = 'topic' in data && typeof record.topic === 'string';
  const hasEvent =
    'event' in data &&
    typeof record.event === 'object' &&
    record.event != null &&
    typeof (record.event as Record<string, unknown>).type === 'string';
  return hasTopic && hasEvent;
};

export type UseWebSocketConnectionOptions = {
  websocketUrl: string;
  isAuthorized: boolean;
  isEnabled: boolean;
  debug?: boolean;
  logger?: WebSocketLogger;
  /** Extra context merged into close/reconnect/error log entries (e.g., `{ isMobileDevice }`). */
  loggerContext?: Record<string, unknown>;
  topicSuffixes: Array<string>;
  onNewMessage?: (message: WebSocketMessage) => void;
};

/**
 * Low-level hook that manages the WebSocket connection lifecycle.
 *
 * Most consumers should use `<WebSocketProvider>` + `useWebSocketContext()` instead.
 * This hook is exported for advanced use cases that need direct control over the
 * connection (e.g., custom providers or non-React integrations).
 *
 * Handles: connecting via `react-use-websocket`, authenticating (`id|` handshake),
 * subscribing/unsubscribing to topic channels, routing incoming messages through
 * type guards, and reconnecting with exponential backoff + jitter.
 */
export const useWebSocketConnection = (options: UseWebSocketConnectionOptions) => {
  const {
    websocketUrl,
    isAuthorized,
    isEnabled,
    debug = false,
    logger,
    loggerContext,
    topicSuffixes,
    onNewMessage,
  } = options;

  const shouldConnectWebSocket = isEnabled && isAuthorized;
  const shouldConnectWebSocketRef = useRef(shouldConnectWebSocket);
  shouldConnectWebSocketRef.current = shouldConnectWebSocket;

  const onNewMessageRef = useRef(onNewMessage);
  onNewMessageRef.current = onNewMessage;

  const loggerRef = useRef(logger);
  loggerRef.current = logger;

  const loggerContextRef = useRef(loggerContext);
  loggerContextRef.current = loggerContext;

  const logDebug = useCallback(
    (message: string, data?: Record<string, unknown>) => {
      if (debug && loggerRef.current) {
        if (data) {
          loggerRef.current.info(message, data);
        } else {
          loggerRef.current.info(message);
        }
      }
    },
    [debug],
  );

  const [authenticatedUserToken, setAuthenticatedUserToken] = useState<string | null>(null);
  const authenticatedUserTokenRef = useRef<string | null>(null);
  const [errorRecoveryCount, setErrorRecoveryCount] = useState(0);
  const subscribedTopicsRef = useRef<Set<string>>(new Set());
  const hadErrorSinceLastCloseRef = useRef(false);
  const reconnectAttemptRef = useRef(0);
  const lastProcessedRef = useRef<MessageEvent | null>(null);
  const [parsedLastMessage, setParsedLastMessage] = useState<WebSocketMessage | null>(null);

  // Authentication functions (defined before handlers that depend on them)
  const resetAuthentication = useCallback(() => {
    authenticatedUserTokenRef.current = null;
    setAuthenticatedUserToken(null);
    subscribedTopicsRef.current.clear();
    setParsedLastMessage(null);
    lastProcessedRef.current = null;
  }, []);

  const authenticateUserRef = useRef<() => void>(() => {});

  // WebSocket event handlers
  const handleWebSocketOpen = useCallback(() => {
    logDebug('WebSocket connected successfully');
    reconnectAttemptRef.current = 0;

    if (hadErrorSinceLastCloseRef.current) {
      setErrorRecoveryCount((prev) => prev + 1);
      hadErrorSinceLastCloseRef.current = false;
    }

    authenticateUserRef.current();
  }, [logDebug]);

  const handleWebSocketError = useCallback(
    // Error is handled in handleWebSocketClose to avoid duplicate error logs
    (error: Event) => {
      logDebug('WebSocket error event received:', {
        type: error.type,
      });
    },
    [logDebug],
  );

  const handleWebSocketClose = useCallback(
    (closeEvent: CloseEvent) => {
      const code = closeEvent.code;
      const isAuthError = code === 1008 || code === 1015;
      const isServerError = code === 1011 || code === 1002 || code === 1003;

      const closeDetails = {
        code,
        reason: closeEvent.reason,
        wasClean: closeEvent.wasClean,
        codeDescription: getWebSocketCloseCodeDescription(code),
      };

      if (isAuthError || isServerError) {
        logError({
          error: new Error(`WebSocket closed: ${isAuthError ? 'auth/policy' : 'server/protocol'} error (code ${code})`),
          context: {
            ...closeDetails,
            ...loggerContextRef.current,
          },
        });
      } else if (loggerRef.current) {
        loggerRef.current.info('WebSocket closed', { ...closeDetails, ...loggerContextRef.current });
      }

      // Only flag an error for non-clean closures so that normal disconnects
      // (e.g., page navigation) don't trigger unnecessary refetches on reconnect.
      // 1000 = normal closure, 1001 = "going away" (page unload/navigation).
      if (!closeEvent.wasClean || (code !== 1000 && code !== 1001)) {
        hadErrorSinceLastCloseRef.current = true;
      }
      resetAuthentication();
    },
    [resetAuthentication],
  );

  const { sendMessage, lastMessage, readyState, getWebSocket } = useWebSocket(
    websocketUrl,
    {
      shouldReconnect: (closeEvent: CloseEvent) => {
        const shouldReconnect = Boolean(shouldConnectWebSocketRef.current);

        if (shouldReconnect) {
          reconnectAttemptRef.current += 1;

          if (loggerRef.current) {
            loggerRef.current.info('WebSocket reconnection attempt', {
              code: closeEvent.code,
              reason: closeEvent.reason,
              wasClean: closeEvent.wasClean,
              attempt: reconnectAttemptRef.current,
              maxAttempts: MAX_RECONNECT_ATTEMPTS,
              ...loggerContextRef.current,
            });
          }

          if (reconnectAttemptRef.current >= MAX_RECONNECT_ATTEMPTS) {
            // Exhausting all reconnect attempts typically reflects a sustained
            // client-side network outage rather than an actionable bug, so
            // report it as info instead of raising a Dragnet exception.
            if (loggerRef.current) {
              loggerRef.current.info('WebSocket reconnect attempts exhausted', {
                code: closeEvent.code,
                attempts: reconnectAttemptRef.current,
                ...loggerContextRef.current,
              });
            }
            return false;
          }
        }

        return shouldReconnect;
      },
      reconnectAttempts: MAX_RECONNECT_ATTEMPTS,
      reconnectInterval: (attempt) => {
        const exp = RECONNECT_INTERVAL * Math.pow(2, attempt);
        // Full jitter: [0, exp] for better thundering-herd protection
        const jitter = exp * Math.random();
        return Math.min(Math.max(jitter, 1000), MAX_RECONNECT_INTERVAL);
      },
      onOpen: handleWebSocketOpen,
      onError: handleWebSocketError,
      onClose: handleWebSocketClose,
      // Browser extensions and user scripts frequently monkey-patch `window.WebSocket` with a proxy;
      // the library's default `instanceof WebSocket` assertion then throws an empty Error and floods
      // Dragnet. The connection still recovers via `shouldReconnect`, so skip the assertion.
      skipAssert: true,
    },
    Boolean(shouldConnectWebSocket), // Only connect if user is authenticated
  );

  const authenticateUser = useCallback(() => {
    const ws = getWebSocket();
    const open = ws?.readyState === WebSocket.OPEN;
    if (!open) {
      logDebug('Skipping auth attempt, socket not yet connected');
      return;
    }
    logDebug('Authenticating user - Requesting userToken');
    sendMessage('id|');
  }, [sendMessage, getWebSocket, logDebug]);
  authenticateUserRef.current = authenticateUser;

  const isConnecting = shouldConnectWebSocket && readyState === ReadyState.CONNECTING;
  const isWebSocketEnabledAndReady =
    shouldConnectWebSocket && readyState === ReadyState.OPEN && !!authenticatedUserToken;

  const handleAuthenticationResponse = useCallback(
    (authResponse: AuthenticationResponse) => {
      if (authenticatedUserTokenRef.current !== authResponse.userToken) {
        logDebug('Got authenticated userToken', { authResponse });
        authenticatedUserTokenRef.current = authResponse.userToken;
        setAuthenticatedUserToken(authResponse.userToken);
      }
    },
    [logDebug],
  );

  // Stabilize topicSuffixes by value so callers don't need to memoize the array.
  const topicSuffixesKey = JSON.stringify(topicSuffixes);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional value-based memoization
  const stableTopicSuffixes = useMemo(() => topicSuffixes, [topicSuffixesKey]);

  // Topic subscription management
  useEffect(() => {
    if (!shouldConnectWebSocket || !authenticatedUserToken || readyState !== ReadyState.OPEN) return;

    // Build desired topics
    const desired = new Set(stableTopicSuffixes.map((s) => `${authenticatedUserToken}${s}`));
    const prev = subscribedTopicsRef.current;

    // Unsubscribe from topics no longer desired.
    // Deleting during Set.forEach is safe per the JS spec — deleted items
    // that haven't been visited yet are skipped.
    prev.forEach((topic) => {
      if (!desired.has(topic)) {
        const req: SubscriptionRequest = { command: 'unsubscribe', topic };
        const frame = `websocketSubscription|${JSON.stringify(req)}`;
        logDebug('Unsubscribing from topic', { topic });
        sendMessage(frame);
        prev.delete(topic);
      }
    });

    // Subscribe to newly desired topics
    desired.forEach((topic) => {
      if (!prev.has(topic)) {
        const req: SubscriptionRequest = { command: 'subscribe', topic };
        const frame = `websocketSubscription|${JSON.stringify(req)}`;
        logDebug('Subscribing to topic', { topic });
        sendMessage(frame);
        prev.add(topic);
      }
    });
  }, [shouldConnectWebSocket, authenticatedUserToken, stableTopicSuffixes, sendMessage, readyState, logDebug]);

  // Message processing
  const handlePushNotification = useCallback(
    (messageData: WebSocketMessage) => {
      logDebug('Push notification received:', messageData);
      logDebug('Event details:', messageData.event);

      if (onNewMessageRef.current) {
        onNewMessageRef.current(messageData);
      }
    },
    [logDebug],
  );

  const processWebSocketMessage = useCallback(
    (messageData: unknown) => {
      if (isAuthenticationResponse(messageData)) {
        handleAuthenticationResponse(messageData);
        return;
      }

      // Handle topic-based messages with event structure
      if (isWebSocketMessage(messageData)) {
        handlePushNotification(messageData);
        return;
      }

      if (loggerRef.current) {
        loggerRef.current.info('Received non-handled WebSocket message', {
          context: typeof messageData === 'object' ? JSON.stringify(messageData) : String(messageData),
        });
      }
    },
    [handleAuthenticationResponse, handlePushNotification],
  );

  // Main message processing effect — parse once and store for both
  // dispatch and the context's lastMessage value.
  useEffect(() => {
    if (!shouldConnectWebSocket || !lastMessage) return;
    // Skip if we've already processed this exact MessageEvent reference.
    // Prevents duplicate dispatch when processWebSocketMessage rebuilds
    // due to upstream callback chain changes (e.g., non-memoized logger).
    if (lastMessage === lastProcessedRef.current) return;
    lastProcessedRef.current = lastMessage;

    try {
      const messageData = safeJsonParse(lastMessage.data);
      if (isWebSocketMessage(messageData)) {
        setParsedLastMessage(messageData);
      }
      processWebSocketMessage(messageData);
    } catch (error) {
      logError({ error, context: 'Failed to parse WebSocket message' });
    }
  }, [shouldConnectWebSocket, lastMessage, processWebSocketMessage]);

  return useMemo(
    () => ({
      errorRecoveryCount,
      isConnecting,
      isWebSocketEnabledAndReady,
      connectionStatus: readyState,
      lastMessage: parsedLastMessage,
      authenticatedUserToken,
    }),
    [
      errorRecoveryCount,
      isConnecting,
      isWebSocketEnabledAndReady,
      readyState,
      parsedLastMessage,
      authenticatedUserToken,
    ],
  );
};
